import React, { useState } from 'react'; // Added useState
import { motion } from 'framer-motion';
import { Check, Crown, Loader2 } from 'lucide-react'; // Added Loader2
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';

const PremiumPage = () => {
  const { user } = useAuth();
  const [isProcessing, setIsProcessing] = useState(false); // New state

  const loadRazorpay = () => {
    return new Promise((resolve) => {
      // Check if script already exists
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.id = 'razorpay-sdk';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleUpgradeClick = async (e) => {
    e.preventDefault(); // Prevent accidental form submission
    
    if (isProcessing) return; // Prevent double clicks
    setIsProcessing(true);

    try {
      const isLoaded = await loadRazorpay();

      if (!isLoaded) {
        alert("Razorpay SDK failed to load. Check your internet connection.");
        setIsProcessing(false);
        return;
      }

      // Check if Key ID exists
      const keyId = process.env.REACT_APP_RAZORPAY_KEY_ID;
      if (!keyId) {
        console.error("Missing REACT_APP_RAZORPAY_KEY_ID in .env");
        alert("Payment system configuration error.");
        setIsProcessing(false);
        return;
      }

      const options = {
        key: keyId,
        amount: 99900, 
        currency: "INR",
        name: "Fitness Tracker",
        description: "Unlock AI Scanner & Pro Features",
        handler: async function (response) {
          const { error } = await supabase
            .from("profiles")
            .update({ 
              premium: true, 
              razorpay_payment_id: response.razorpay_payment_id 
            })
            .eq("id", user.id);

          if (error) {
            console.error("Update error:", error);
            alert("Payment received but profile update failed.");
          } else {
            alert("Welcome to Pro!");
            window.location.href = "/dashboard";
          }
        },
        prefill: {
          email: user?.email || "",
        },
        theme: {
          color: "#10b981",
        },
        modal: {
          ondismiss: function() {
            setIsProcessing(false); // Reset loading if user closes modal
          }
        }
      };

      const paymentObject = new window.Razorpay(options);
      paymentObject.open();
    } catch (err) {
      console.error("Razorpay Error:", err);
      setIsProcessing(false);
    }
  };

const freeFeatures = [
  'Calorie Tracking',
  'Water Tracking',
  'Step Tracking',
  'BMI Calculator',
  'Daily Weight Logs',
  'Basic Health Stats'
];

const proFeatures = [
  ...freeFeatures,
  'AI Food Scanner',
  'Personalised Diet',
  'Smart Workout Engine',
  'Advanced Macro Analysis',
  'Priority Support',
  
];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
      className="max-w-5xl mx-auto space-y-10 py-10 px-4"
    >
      <div className="text-center">
        <h1 className="text-4xl font-bold text-gray-900">Choose Your Plan</h1>
        <p className="mt-2 text-lg text-gray-600">Upgrade to unlock AI-powered tools.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* FREE PLAN CARD (Condensed) */}
        <div className="bg-white rounded-2xl p-8 border border-gray-200">
          <h2 className="text-2xl font-semibold">Free Plan</h2>
          <p className="text-5xl font-bold mt-4">₹0</p>
          <ul className="mt-8 space-y-4">
             {freeFeatures.map(f => <li key={f} className="flex gap-2"><Check className="text-emerald-500" />{f}</li>)}
          </ul>
        </div>
        
        {/* PRO PLAN CARD */}
        <motion.div className="bg-gray-900 rounded-2xl p-8 shadow-2xl flex flex-col relative overflow-hidden">
          <div className="absolute top-0 right-0 px-4 py-2 bg-amber-400 text-amber-900 text-sm font-bold rounded-bl-2xl flex items-center gap-1">
            <Crown size={16} /> Popular
          </div>
          <h2 className="text-2xl font-semibold text-white">Pro Plan</h2>
          <div className="mt-4">
            <span className="text-5xl font-bold text-white">₹999</span>
            <span className="text-gray-400"> / Year</span>
          </div>

          <button
            onClick={handleUpgradeClick}
            disabled={isProcessing}
            className="mt-6 w-full py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {isProcessing ? <Loader2 className="animate-spin" /> : "Upgrade Now"}
          </button>

          <ul className="mt-8 space-y-4 text-gray-300">
             {proFeatures.map(f => <li key={f} className="flex gap-2"><Check className="text-emerald-400" />{f}</li>)}
          </ul>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default PremiumPage;